import type { Amenity } from '@aia/api';

type AmenityGroup = {
  amenities: Array<AmenityWithPresence>;
  key: ValidCategoryKey;
};

export type AmenityWithPresence = Amenity & { present: boolean };

// Ordered list of valid category keys, used to ensure the correct order of categories when
// grouping amenities.
const categories = [
  'area_views',
  'bathroom',
  'bedroom',
  'entertainment',
  'family',
  'heating',
  'home_safety',
  'internet_and_office',
  'kitchen',
  'location',
  'outdoor',
  'parking',
  'services',
  'other',
] as const;

export type ValidCategoryKey = (typeof categories)[number];

function isVisibleInList(amenity: AmenityWithPresence): boolean {
  // Never show amenities with NEVER list visibility
  if (amenity.listVisibility === 'list_never') {
    return false;
  }

  // Only show IF_PRESENT amenities when they are present
  if (!amenity.present && amenity.listVisibility === 'list_if_present') {
    return false;
  }

  // Only show IF_NOT_PRESENT amenities when they are not present
  if (amenity.present && amenity.listVisibility === 'list_if_not_present') {
    return false;
  }

  return true;
}

// Groups the amenities by their category, sorts the categories alphabetically, and filters out
// amenities that should not be shown based on their list visibility.
export function groupAmenities(amenities: Array<AmenityWithPresence>): Array<AmenityGroup> {
  const amenitiesGroups: Array<AmenityGroup> = categories.map((key) => ({
    key,
    amenities: [],
  }));

  // Add amenities to their respective groups based on their category key. If an amenity does not
  // have a category key, it is added to the "other" group.
  amenities.forEach((amenity) => {
    const categoryKey = (amenity.category?.key as ValidCategoryKey) || 'other';
    const group: AmenityGroup = amenitiesGroups.find((group) => group.key === categoryKey)!;

    if (isVisibleInList(amenity)) {
      group.amenities.push(amenity);
    }
  });

  // Remove groups that have no amenities
  return amenitiesGroups.filter((group) => group.amenities.length > 0);
}
